import { IconButton } from '@creditornot/cb-button';
import { Cross } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuCheckboxItem } from '@creditornot/cb-menu';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { DecodedValueMap, SetQuery } from 'use-query-params';

import { AnalyticsFilter } from 'apis/merchant-gateway/merchant-analytics/types';
import { isAnalyticsFilter } from 'apis/merchant-gateway/merchant-analytics/utils';
import { typographyCss } from 'common/typography';
import { RatingSmiley } from 'modules/insights-v2/components/ratings/Smiley';

import { RATING_TEXTS } from './constants';
import { PurchasesQueryConfig, VIEW_TYPE } from './purchasesQueryConfig';

const Container = styled.div`
  width: 330px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  padding-block-end: 8px;
`;

const Heading = styled.div`
  margin: 0 16px;
  padding: 12px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-block-end: 2px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const HeadingText = styled.h2`
  ${typographyCss.Body2}
  margin: 0;
  font-weight: bold;
`;

const CheckboxRow = styled.div`
  padding: 4px 8px;
`;

const RatingsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 16px 8px;
`;

const RatingText = styled.span`
  ${typographyCss.Small}
  font-weight: 500;
  display: block;
`;

const Smiley = styled(RatingSmiley)`
  width: 24px;
  height: 24px;
`;
const SmileyColored = styled(Smiley).attrs({ variant: 'colored' })``;
const SmileyOutline = styled(Smiley).attrs({ variant: 'outline' })``;
const RatingButton = styled.button<{ checked: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 56px;

  border: none;
  border-radius: 8px;
  background: none;
  padding: 0;
  cursor: pointer;
  outline: none;

  color: ${cbSemanticCssVars.textSecondary};

  ${SmileyColored} {
    display: none;
  }
  ${SmileyOutline} {
    display: block;
  }

  &:hover,
  &:active {
    color: ${cbSemanticCssVars.textPrimary};

    ${SmileyColored} {
      display: block;
    }
    ${SmileyOutline} {
      display: none;
    }
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${cbSemanticCssVars.a11yFocusIndicator};
  }

  ${({ checked }) =>
    checked &&
    css`
      color: ${cbSemanticCssVars.textPrimary};

      ${SmileyColored} {
        display: block;
      }
      ${SmileyOutline} {
        display: none;
      }
    `}
`;

type FiltersContentProps = {
  query: DecodedValueMap<PurchasesQueryConfig>;
  setQuery: SetQuery<PurchasesQueryConfig>;
  onClose?: () => void;
};

const ratingFilters: { rating: number; filter: AnalyticsFilter }[] = [
  { rating: 1, filter: 'with_rating_1' },
  { rating: 2, filter: 'with_rating_2' },
  { rating: 3, filter: 'with_rating_3' },
  { rating: 4, filter: 'with_rating_4' },
  { rating: 5, filter: 'with_rating_5' },
];

const FiltersContent = ({ setQuery, query, onClose }: FiltersContentProps) => {
  const filters = query.filters ? query.filters.split(',').filter(isAnalyticsFilter) : [];

  const isFilterChecked = (filter: AnalyticsFilter) => filters.includes(filter);

  const handleFilterChange = (changedFilter: AnalyticsFilter) => {
    const newFilters = isFilterChecked(changedFilter)
      ? filters.filter(filter => filter !== changedFilter)
      : [...new Set([...filters, changedFilter])];

    setQuery({ filters: newFilters.length > 0 ? newFilters.join(',') : undefined });
  };

  return (
    <Container>
      <Heading>
        <HeadingText>
          <FormattedMessage id="purchases.status" />
        </HeadingText>
        <IconButton size="tiny" variant="neutral" alignment="boundingBox" onClick={onClose}>
          <Cross />
        </IconButton>
      </Heading>
      {query.view === VIEW_TYPE.orders && (
        <CheckboxRow>
          <MenuCheckboxItem checked={isFilterChecked('active')} onClick={() => handleFilterChange('active')}>
            <FormattedMessage id="purchases.filters.order-status-active" />
          </MenuCheckboxItem>
        </CheckboxRow>
      )}
      <CheckboxRow>
        <MenuCheckboxItem checked={isFilterChecked('completed')} onClick={() => handleFilterChange('completed')}>
          <FormattedMessage id="purchases.delivery-statuses.delivered" />
        </MenuCheckboxItem>
      </CheckboxRow>
      <CheckboxRow>
        <MenuCheckboxItem checked={isFilterChecked('rejected')} onClick={() => handleFilterChange('rejected')}>
          <FormattedMessage id="purchases.delivery-statuses.rejected" />
        </MenuCheckboxItem>
      </CheckboxRow>
      {query.view === VIEW_TYPE.orders && (
        <>
          <Heading>
            <HeadingText>
              <FormattedMessage id="purchase-history.ratings-filter.title" />
            </HeadingText>
          </Heading>

          <RatingsContainer>
            {ratingFilters.map(({ rating, filter }) => (
              <RatingButton key={filter} checked={isFilterChecked(filter)} onClick={() => handleFilterChange(filter)}>
                <SmileyColored rating={rating} />
                <SmileyOutline rating={rating} />
                <RatingText>
                  <FormattedMessage id={RATING_TEXTS[rating]} />
                </RatingText>
              </RatingButton>
            ))}
          </RatingsContainer>

          <Heading>
            <HeadingText>
              <FormattedMessage id="purchases.labels.reviews" />
            </HeadingText>
          </Heading>

          <CheckboxRow>
            <MenuCheckboxItem
              checked={isFilterChecked('with_written_review')}
              onClick={() => handleFilterChange('with_written_review')}
            >
              <FormattedMessage id="purchases.filters.ordersWithWrittenReviews" />
            </MenuCheckboxItem>
          </CheckboxRow>
        </>
      )}
    </Container>
  );
};

export default FiltersContent;
